import { useCallback } from 'react'
import { useImageGeneration } from './useCopywriting'
import { useR2Upload } from '@/lib/r2'
import {
  normalizeAndUploadImage as normalizeAndUploadImageUtil,
  normalizeAndUploadReferenceImage as normalizeAndUploadReferenceImageUtil,
  resolveReferenceImageUrl,
  type UploadFunction,
} from '@/lib/referenceImage'
import { callWithRetry } from '@/lib/semaphore'
import {
  collageLayoutMap,
  resolveAssetsForItem,
  resolveSourceImagesFromPlan,
} from '@/lib/imageUtils'
import { generateCollage, getRequiredImageCount } from '@/lib/collage'
import type { Asset, Note, NoteImage } from '@/types'
import type { ImagePlanItem } from '@/types/pipeline'

export function usePipelineGenerator() {
  const { generateImage, isGenerating, error: genError } = useImageGeneration()

  // R2 上传 hook
  const { startUpload: startCollageUpload } = useR2Upload({
    onClientUploadComplete: (res) => {
      console.log('✅ Base64 图片上传完成:', res)
    },
    onUploadError: (error) => {
      console.error('❌ Base64 图片上传失败:', error)
    },
  })

  // 上传函数包装
  const uploadFn: UploadFunction = startCollageUpload

  // 规范化并上传图片
  const normalizeAndUploadImage = useCallback(
    async (urlOrBase64: string, noteId: string, imageIndex: number): Promise<string> => {
      return normalizeAndUploadImageUtil(urlOrBase64, noteId, imageIndex, uploadFn)
    },
    [uploadFn]
  )

  // 规范化并上传参考图
  const normalizeAndUploadReferenceImage = useCallback(
    async (
      urlOrBase64: string | undefined,
      noteId: string,
      imageIndex: number
    ): Promise<string | undefined> => {
      return normalizeAndUploadReferenceImageUtil(urlOrBase64, noteId, imageIndex, uploadFn)
    },
    [uploadFn]
  )

  // 单张配图生成主逻辑
  const processImageItem = useCallback(
    async (
      note: Note,
      item: ImagePlanItem,
      analyzedAssets: Asset[],
      updateImagesForNote: (noteId: string, updater: (prev: NoteImage[]) => NoteImage[]) => Promise<void>
    ): Promise<void> => {
      const productionMethod = item.production_method || 'collage'

      // 解析参考图 URL
      const referenceImageUrl = await resolveReferenceImageUrl({
        referenceImageId: item.reference_image_id,
        referenceImage: item.reference_image,
        assets: analyzedAssets,
        uploadFn,
      })

      // 1. asset - 直接使用单张素材
      if (productionMethod === 'asset') {
        const assetId = item.asset_ids?.[0]
        const asset = analyzedAssets.find((a) => a.id === assetId) || analyzedAssets[0]
        await updateImagesForNote(note.id, (prev) =>
          prev.map((img) =>
            img.index === item.index
              ? {
                  ...img,
                  status: 'completed',
                  source: 'asset',
                  url: asset.url,
                  asset_ids: [asset.id],
                  production_method: 'asset',
                }
              : img
          )
        )
        return
      }

      // 2. collage - 多张素材拼图
      if (productionMethod === 'collage') {
        const layout = collageLayoutMap[item.type]
        const need = layout ? getRequiredImageCount(layout) : 4
        const pickedAssets = resolveAssetsForItem(item.asset_ids, need, analyzedAssets)

        if (pickedAssets.length < need) {
          throw new Error('可用素材不足，无法合成拼图')
        }

        // 拼图生成带降质策略
        let collageBase64: string
        try {
          collageBase64 = await generateCollage({
            layout: layout || '2x2',
            images: pickedAssets.map((a) => a.url),
            options: { width: 1080, height: 1440, quality: 0.92 },
          })
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : String(err)
          
          if (errorMessage.includes('CORS')) {
            console.error('[拼图生成失败] CORS 配置问题，无法重试:', errorMessage)
            throw new Error(`拼图生成失败：${errorMessage}`)
          }
          
          console.warn('[拼图生成失败] 尝试降低质量重试:', errorMessage)
          try {
            collageBase64 = await generateCollage({
              layout: layout || '2x2',
              images: pickedAssets.map((a) => a.url),
              options: { width: 900, height: 1200, quality: 0.8 },
            })
          } catch (retryErr) {
            const retryMessage = retryErr instanceof Error ? retryErr.message : String(retryErr)
            throw new Error(`拼图生成失败（已重试）：${retryMessage}`)
          }
        }

        const collageUrl = await normalizeAndUploadImage(collageBase64, note.id, item.index)

        await updateImagesForNote(note.id, (prev) =>
          prev.map((img) =>
            img.index === item.index
              ? {
                  ...img,
                  status: 'completed',
                  source: 'collage',
                  url: collageUrl,
                  production_method: 'collage',
                }
              : img
          )
        )
        return
      }

      // 3. ai_generated - AI 生成图片
      if (productionMethod === 'ai_generated') {
        const prompt =
          item.generation_prompt ||
          `${note.title}｜请生成符合小红书风格的配图，重点：${item.description || '突出主题'}`.slice(
            0,
            200
          )

        const sourceImages = resolveSourceImagesFromPlan(item.asset_ids, analyzedAssets)

        const aiUrl = await callWithRetry(
          () =>
            generateImage(prompt, {
              aspect_ratio: '3:4',
              image_text: item.image_text || '',
              source_images: sourceImages,
              reference_image: referenceImageUrl,
              note_id: note.id,
              image_index: item.index,
            }),
          {
            timeout: 180000,
            retries: 2,
            onRetry: (attempt) => {
              console.warn(`[AI生成] 第 ${attempt} 次重试`)
            },
          }
        )
        const finalUrl = await normalizeAndUploadImage(aiUrl, note.id, item.index)

        if (!finalUrl) {
          throw new Error('AI 未返回图片')
        }

        await updateImagesForNote(note.id, (prev) =>
          prev.map((img) =>
            img.index === item.index
              ? {
                  ...img,
                  status: 'completed',
                  source: 'ai_generated',
                  url: finalUrl,
                  generation_prompt: prompt,
                  production_method: 'ai_generated',
                }
              : img
          )
        )
        return
      }

      // 4. collage_then_ai - 先拼图再 AI 生成
      if (productionMethod === 'collage_then_ai') {
        const layout = collageLayoutMap[item.type]
        const need = layout ? getRequiredImageCount(layout) : 4
        const pickedAssets = resolveAssetsForItem(item.asset_ids, need, analyzedAssets)

        if (pickedAssets.length < need) {
          throw new Error('可用素材不足，无法合成拼图')
        }

        const collageBase64 = await generateCollage({
          layout: layout || '2x2',
          images: pickedAssets.map((a) => a.url),
          options: { width: 1080, height: 1440, quality: 0.92 },
        })

        const prompt =
          item.generation_prompt ||
          `在这张拼图上添加标题 and 装饰，标题：${note.title}`.slice(0, 200)

        const aiUrl = await callWithRetry(
          () =>
            generateImage(prompt, {
              aspect_ratio: '3:4',
              image_text: item.image_text || '',
              source_images: [collageBase64],
              reference_image: referenceImageUrl,
              note_id: note.id,
              image_index: item.index,
            }),
          {
            timeout: 180000,
            retries: 2,
            onRetry: (attempt) => {
              console.warn(`[AI生成-拼图加字] 第 ${attempt} 次重试`)
            },
          }
        )
        const finalUrl = await normalizeAndUploadImage(aiUrl, note.id, item.index)

        if (!finalUrl) {
          throw new Error('AI 未返回图片')
        }

        await updateImagesForNote(note.id, (prev) =>
          prev.map((img) =>
            img.index === item.index
              ? {
                  ...img,
                  status: 'completed',
                  source: 'ai_generated',
                  url: finalUrl,
                  generation_prompt: prompt,
                  production_method: 'collage_then_ai',
                }
              : img
          )
        )
        return
      }
    },
    [generateImage, normalizeAndUploadImage, uploadFn]
  )

  return {
    processImageItem,
    normalizeAndUploadImage,
    normalizeAndUploadReferenceImage,
    isGenerating,
    genError,
    uploadFn,
  }
}

